import type { LucideIcon } from "lucide-react";
import {
  Building2,
  Car,
  CheckCircle2,
  FileCheck2,
  Home,
  Leaf,
  MapPinned,
  ShieldCheck,
  Sparkles,
  Users,
  Zap,
} from "lucide-react";

export type NewsHighlight = {
  title: string;
  description: string;
  Icon: LucideIcon;
};

export type NewsFeature = {
  title: string;
  Icon: LucideIcon;
};

export type NewsKeyMessage = {
  title: string;
  description: string;
  Icon: LucideIcon;
};

export const newsHero = {
  title: "News & Updates",
  subtitle:
    "Stay informed with the latest stories, partnerships, and innovations from the world of community mobility.",
} as const;

export const delhiArticle = {
  id: "delhi-anti-pollution",
  tag: "Sustainability",
  date: "Aug 08, 2026",
  readTime: "5 min read",
  title:
    "What is Delhi’s new anti-pollution push? Hint: Private EV taxis, shared rides, fewer cars and more",
  description:
    "Delhi is taking bold steps to combat rising pollution levels through a multi-pronged strategy focused on sustainable mobility. The government is encouraging private EV taxis, promoting shared rides, and reducing dependency on single-occupancy vehicles.",
  keyPoints: [
    "Incentives for EV taxis to boost clean transportation",
    "Promotion of carpooling and shared mobility",
    "Reduction of unnecessary private vehicle usage",
    "Focus on long-term green urban mobility",
  ],
  readMoreUrl:
    "https://www.timesnownews.com/delhi/what-is-delhis-new-anti-pollution-push-hint-private-ev-taxis-shared-rides-fewer-cars-and-more-article-153388636",
  videoLabel: "Video",
  videoDuration: "02:45",
} as const;

export const delhiHighlights: NewsHighlight[] = [
  {
    title: "Promoting EV taxis",
    description: "Clean transport solutions",
    Icon: Zap,
  },
  {
    title: "Shared rides",
    description: "Fewer vehicles on road",
    Icon: Users,
  },
  {
    title: "Policy changes",
    description: "Reduce emissions",
    Icon: FileCheck2,
  },
  {
    title: "Green future",
    description: "Sustainable city mobility",
    Icon: Leaf,
  },
];

export type NewsInsight = {
  title: string;
  description: string;
  Icon: LucideIcon;
};

export const conclaveArticle = {
  id: "kr-bharat-conclave",
  tag: "Keynote & Conclave",
  date: "Jul 26, 2026",
  readTime: "5 min read",
  title:
    "Mohan Nanduri Addresses KR Bharat Conclave 2026: Redefining Mobility, Trust, and India’s Sharing Economy",
  description:
    "Speaking before industry leaders, policymakers, and innovators at the KR Bharat Conclave 2026 at T-Hub Hyderabad, Mohan Nanduri, CEO of Duri App and Founder & CTO of HansAI Technosoft, delivered a keynote on transforming Indian transportation through verified ridesharing and sustainable green mobility.",
  keyPoints: [
    "Unlocking 350M+ registered vehicles to eliminate empty commute seats",
    "10,000 shared rides achieves an emission offset equal to 1M trees",
    "Instant DigiLocker & live Vahan API checks in under 2 minutes",
    "Women-first safety: gender-preference matching & 1-tap emergency SOS",
    "MVA 1988 compliance: non-profit cost-splitting capped by AI Fare Engine",
  ],
  insights: [
    {
      title: "The Mobility Deficit & 2047 Vision",
      description:
        "India has over 350 million registered vehicles, yet millions of passenger seats run empty every day. During festival rushes across Telangana and Andhra Pradesh, hubs like MGBS and Secunderabad Station reach breaking capacity. Unlocking empty car seats through safe, verified ridesharing provides immediate relief.",
      Icon: Car,
    },
    {
      title: "Real-Time Government API Trust Engine",
      description:
        "To overcome commuter hesitation, Duri integrates automated verification: instant DigiLocker and Aadhaar identity authentication, real-time Ministry of Road Transport Vahan database checks in under two minutes (verifying registration, DL, insurance, and PUC), and dedicated women's safety features with gender-preference matching.",
      Icon: ShieldCheck,
    },
    {
      title: "Legal Compliance & AI Fare Engine",
      description:
        "Operating strictly under Motor Vehicles Act 1988 provisions, Duri's AI-Powered Fare Engine factors exact route distance, vehicle mileage, and daily fuel prices to establish an unalterable cost-split ceiling—ensuring private car owners share legitimate expenses rather than earn commercial profits.",
      Icon: FileCheck2,
    },
  ] as NewsInsight[],
  founder: {
    name: "Mohan Nanduri",
    role: "Founder & CTO of HansAI | CEO of Duri App",
    intro:
      "Addressing innovators and policymakers at T-Hub Hyderabad, Mohan outlined how community ridesharing, verified identity, and green mobility solve India's toughest transportation bottlenecks while advancing the vision of Viksit Bharat 2047.",
    engineeringNote:
      "The entire Duri platform was architected and built in Hyderabad at HansAI's Gachibowli BOT center by local engineering talent, replacing unorganized carpool groups with automated daily commute workflows.",
  },
  contributions: [
    "Vikasit Bharat 2047: Sustainable mobility as an economic growth pillar",
    "Eco Milestone: 10,000 shared rides offsets emissions equal to 1M trees",
    "Trust Engine: Real-time Vahan & DigiLocker checks in under 2 minutes",
    "Women's Safety: Gender-preference matching, seat selection & SOS",
    "Fair Compliance: MVA 1988 cost-splitting capped by AI Fare Engine",
    "Make in India: 100% built at HansAI Hyderabad BOT Center",
  ],
  featuresHeading: "Core Innovations Built Into Duri",
  features: [
    { title: "DigiLocker & Aadhaar KYC", Icon: ShieldCheck },
    { title: "Live Vahan Database Checks", Icon: FileCheck2 },
    { title: "Women-First Safety Matching", Icon: Users },
    { title: "MVA 1988 Fair Cost-Splitting", Icon: Car },
    { title: "Automated Daily Commute", Icon: Building2 },
    { title: "CO₂ & ESG Green Tracking", Icon: Leaf },
  ] as NewsFeature[],
  videoLabel: "Official Keynote",
  videoDuration: "20:15",
  youtubeId: "RnawmTffczc",
  videoPoster: "/images/kr-bharat-conclave-2026.webp",
  youtubeUrl: "https://www.youtube.com/watch?v=RnawmTffczc",
} as const;


export const pmArticle = {
  id: "pm-wfh-carpooling",
  tag: "National",
  date: "Jul 20, 2026",
  readTime: "6 min read",
  title:
    "India’s Prime Minister Urges Citizens To Adopt WFH & Carpooling To Conserve Fuel",
  description:
    "In a strong message to the nation, the Prime Minister encouraged citizens, organizations, and businesses to adopt Work From Home (WFH) and carpooling practices to reduce fuel consumption and ease traffic congestion.",
  keyMessagesHeading: "Key Messages from the PM",
  keyMessages: [
    {
      title: "Work From Home",
      description: "Reduces fuel usage",
      Icon: Home,
    },
    {
      title: "Carpooling",
      description: "Decreases traffic congestion",
      Icon: Car,
    },
    {
      title: "Lower emissions",
      description: "Help improve air quality",
      Icon: Leaf,
    },
    {
      title: "National impact",
      description: "Small actions lead the way",
      Icon: Users,
    },
    {
      title: "Greener future",
      description: "Sustainable habits that last",
      Icon: CheckCircle2,
    },
  ] as NewsKeyMessage[],
  videoDuration: "02:30",
} as const;

export const newsSubscribe = {
  text: "Stay updated with the latest news, stories, and insights on community mobility and sustainability.",
  placeholder: "Enter your email",
  button: "Subscribe",
} as const;

export const newsQuote = {
  text: "Community-driven mobility is how we move toward cleaner cities, fairer fares, and a sustainable future — together.",
} as const;
